import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";

import { FaRegCheckCircle } from "react-icons/fa";
import { HiOutlineUpload, HiOutlineDownload } from "react-icons/hi";
import { FiEdit } from "react-icons/fi";

import { toast } from "react-toastify";
import { TUTOR_COURSE_API, TUTOR_ASSIGNMENT_API } from "../../../api/api";

import TemplateCSV from "../../../assets/Mcq's-template.csv";

import "./TutorCreateAssignments.css";

const TutorCreateAssignments = ({
  show,
  onHide,
  editingAssignment,
  fetchAssignments,
}) => {
  const [step, setStep] = useState(1);
  const [activeTab, setActiveTab] = useState("manual");

  const [formData, setFormData] = useState({
    assignmentTitle: "",
    course: "",
    module: "",
    assignmentType: "",
    totalQuestions: "",
    totalMarks: "",
    passingPercentage: 60,
  });

  // Questions
  const [manualQuestions, setManualQuestions] = useState([]);
  const [importQuestions, setImportQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    questionText: "",
    options: ["", "", "", ""],
    correctOptionIndex: null,
  });

  const [courseModules, setCourseModules] = useState({});
  const [questions, setQuestions] = useState([]);

  const [editingIndex, setEditingIndex] = useState(null);

  //loading
  const [isCreateAssignmentLoading, setIsCreateAssignmentLoading] =
    useState(false);
  const [isUpdateQuestionLoading, setIsUpdateQuestionLoading] = useState(false);

  const token = localStorage.getItem("token");

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const showInfoToast = (message) => {
    toast.info(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  const handleCreateAssignmentChange = (e) => {
    const { name, value } = e.target;

    if (name === "course") {
      setFormData((prev) => ({
        ...prev,
        course: value,
        module: "", // Reset the module selection
      }));
    } else if (name === "module") {
      setFormData((prev) => ({
        ...prev,
        module: value,
      }));
    } else if (name === "totalQuestions") {
      setFormData((prev) => ({
        ...prev,
        totalQuestions: value,
        totalMarks: value, // Automatically set marks = total questions
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Manual question handlers
  const handleQuestionChange = (e) => {
    setNewQuestion((prev) => ({ ...prev, questionText: e.target.value }));
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...newQuestion.options];
    updatedOptions[index] = value;
    setNewQuestion((prev) => ({ ...prev, options: updatedOptions }));
  };

  const setCorrectOption = (index) => {
    setNewQuestion((prev) => ({ ...prev, correctOptionIndex: index }));
  };

  const handleCloseModal = () => {
    setStep(1); // Go back to Step 1
    setActiveTab("manual"); // Default to manual tab
    setEditingIndex(null);
    setManualQuestions([]); // Reset questions lists (both manual and imported)
    setImportQuestions([]);
    setQuestions([]);

    // Reset the form data to initial state
    setFormData({
      assignmentTitle: "",
      course: "",
      module: "",
      assignmentType: "",
      totalQuestions: "",
      totalMarks: "",
      passingPercentage: 60,
    });

    // Reset the new question state
    setNewQuestion({
      questionText: "",
      options: ["", "", "", ""],
      correctOptionIndex: null,
    });

    // Close the modal
    onHide();
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      showErrorToast("User not found. Please log in again.");
      return;
    }

    const fetchCourses = async () => {
      try {
        const response = await axios.post(
          TUTOR_COURSE_API.GET_TUTOR_COURSES_WITH_MODULES,
          { tutorid: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const { statusCode, message, data } = response.data;

        if (statusCode === 200 && Array.isArray(data)) {
          // Map the courses and modules
          const courses = data.reduce((acc, course) => {
            acc[course.course_id] = {
              title: course.course_title,
              modules: course.modules.map((module) => ({
                module_id: module.module_id,
                title: module.module_title,
              })),
            };
            return acc;
          }, {});

          // Set courses and modules into state
          setCourseModules(courses);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(message);
        }
      } catch (error) {
        console.error("Error fetching courses and modules:", error);
        // Server responded with non-2xx status (like 400, 500)
        if (error.response && error.response.data) {
          showErrorToast(error.response.data.message);
        } else {
          showErrorToast("Network error! Please try again.");
        }
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    if (editingAssignment) {
      setStep(2); // Jump directly to step 2
      setManualQuestions(editingAssignment.questions || []);
      setFormData({
        assignmentId: editingAssignment.assignmentId || "",
        assignmentTitle: editingAssignment.assignmentTitle || "",
        assignmentType: editingAssignment.assignmentType || "",
        totalQuestions: editingAssignment.totalQuestions || "",
        totalMarks: editingAssignment.totalMarks || "",
        passingPercentage: editingAssignment.passPercentage || "60",
      });
    }
  }, [editingAssignment]);

  const handleNextStep = async () => {
    // Validate form fields
    if (
      !formData.assignmentTitle ||
      !formData.course ||
      !formData.module ||
      !formData.assignmentType ||
      !formData.totalQuestions ||
      !formData.totalMarks ||
      !formData.passingPercentage
    ) {
      showErrorToast("Please fill all fields");
      return;
    }

    if (!/^[A-Za-z].*/.test(formData.assignmentTitle)) {
      showErrorToast("Assignment title must start with at least one letter!");
      return;
    }

    setStep((s) => Math.min(3, s + 1));
  };

  const addManualQuestion = () => {
    const { questionText, options, correctOptionIndex } = newQuestion;

    //Total Questions limit check
    if (manualQuestions.length >= Number(formData.totalQuestions)) {
      showInfoToast(`You can only add ${formData.totalQuestions} questions!`);
      return;
    }

    if (
      questionText.trim() &&
      options.every((opt) => opt.trim()) &&
      correctOptionIndex !== null
    ) {
      //  Check for unique options

      //  Question must contain at least one letter
      if (!/\S/.test(questionText)) {
        showInfoToast("Question cannot be empty.");
        return;
      }

      // Option must contain at least one alphabet
      for (let opt of options) {
        if (!/\S/.test(opt)) {
          showInfoToast("Question cannot be empty.");
          return;
        }
      }

      //  Check duplicate options inside same question
      const normalizedOptions = options.map((opt) => opt.trim());
      const optionSet = new Set(normalizedOptions);

      if (optionSet.size !== options.length) {
        showInfoToast("Options must be unique.");
        return; // stop here if duplicate options
      }

      //  Prevent duplicate question + options (ignore order)
      const sortedNewOptions = [...normalizedOptions].sort();

      const isDuplicate = manualQuestions.some((q, index) => {
        if (editingIndex !== null && index === editingIndex) return false;

        const sameQuestion =
          q.questionText.trim().toLowerCase() ===
          questionText.trim().toLowerCase();

        const existingSortedOptions = q.options
          .map((o) => o.trim().toLowerCase())
          .sort();

        const sameOptions =
          JSON.stringify(existingSortedOptions) ===
          JSON.stringify(sortedNewOptions);

        return sameQuestion && sameOptions;
      });

      if (isDuplicate) {
        showInfoToast("This question with same options already exists.");
        return;
      }

      if (editingIndex !== null) {
        // Update existing question
        const updatedQuestions = [...manualQuestions];
        updatedQuestions[editingIndex] = newQuestion;
        setManualQuestions(updatedQuestions);
        setEditingIndex(null); // reset editing state
        // toast.success("Question added successfully!", {
        //   position: "top-right",
        //   autoClose: 1500,
        //   theme: "colored",
        // });
      } else {
        // Add new question
        setManualQuestions((prev) => [...prev, newQuestion]);
        // toast.success("Question added successfully!", {
        //   position: "top-right",
        //   autoClose: 1500,
        //   theme: "colored",
        // });
      }

      // Clear inputs
      setNewQuestion({
        questionText: "",
        options: ["", "", "", ""],
        correctOptionIndex: null,
      });
    } else {
      toast.info(
        "Please fill question, all options and select the correct option.",
        { position: "top-right", autoClose: 1500, theme: "colored" },
      );
    }
  };

  const handleEditQuestion = (index) => {
    setStep(2);
    setActiveTab("manual");
    setEditingIndex(index);
    setNewQuestion({ ...manualQuestions[index] });
  };

  const removeManualQuestion = (index) => {
    const updated = [...manualQuestions];
    updated.splice(index, 1);
    setManualQuestions(updated);
  };

  const removeImportQuestion = (index) => {
    const updated = [...importQuestions];
    updated.splice(index, 1);
    setImportQuestions(updated);
  };

  const handleSaveQuestions = () => {
    if (manualQuestions.length === 0 && importQuestions.length === 0) {
      toast.info("Please add some questions.", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
      });
      return;
    }

    const allQuestions = manualQuestions;
    setQuestions(allQuestions);

    // Move to Step 3, ensuring it doesn't go beyond Step 3
    setStep((s) => Math.min(3, s + 1));

    toast.success("Questions saved successfully!", {
      position: "top-right",
      autoClose: 2000,
      theme: "colored",
    });
  };

  const handleUpdateRejectedQuestion = async () => {
    if (!editingAssignment?.assignmentId)
      return showErrorToast("assignmentId not found");

    if (!token) {
      showErrorToast("User not found. Please log in again.");
      return;
    }

    setIsUpdateQuestionLoading(true);

    const payload = {
      assignment_id: editingAssignment.assignmentId,
      questions: manualQuestions.map((q) => ({
        question_id: q.questionId,
        question: q.questionText,
        a: q.options[0],
        b: q.options[1],
        c: q.options[2],
        d: q.options[3],
        answer: ["a", "b", "c", "d"][q.correctOptionIndex].toLowerCase(),
      })),
    };

    try {
      const response = await axios.post(
        TUTOR_ASSIGNMENT_API.UPDATE_REJECTED_QUESTIONS,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;
      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });
        fetchAssignments();
        // close modal after update
        handleCloseModal();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(response.data.message);
      }
    } catch (error) {
      console.error("Error updating question:", error);

      // Server responded with non-2xx status (like 400, 500)
      if (error.response && error.response.data) {
        showErrorToast(error.response.data.message);
      } else {
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setIsUpdateQuestionLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (
      !formData.assignmentTitle ||
      !formData.course ||
      !formData.module ||
      !formData.assignmentType ||
      !formData.totalQuestions ||
      !formData.totalMarks ||
      !questions.length
    ) {
      showErrorToast("Please fill all assignment fields and add questions.");
      return;
    }

    if (!token) {
      showErrorToast("User not found. Please log in again.");
      return;
    }

    setIsCreateAssignmentLoading(true);

    // Map questions into API format
    const optionKeys = ["a", "b", "c", "d"];
    const questionsPayload = questions.map((q) => ({
      question: q.questionText,
      a: q.options[0],
      b: q.options[1],
      c: q.options[2],
      d: q.options[3],
      answer: optionKeys[q.correctOptionIndex],
    }));

    const payload = {
      course_id: formData.course,
      module_id: formData.module,
      assignment_title: formData.assignmentTitle,
      assignment_type: formData.assignmentType,
      total_questions: formData.totalQuestions,
      total_marks: formData.totalMarks,
      pass_percentage: formData.passingPercentage,
      questions: questionsPayload,
    };

    try {
      const response = await axios.post(
        TUTOR_ASSIGNMENT_API.ADD_ASSIGNMENT_WITH_QUESTIONS,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });
        fetchAssignments();
        // Reset everything after successful creation
        setQuestions([]);
        handleCloseModal();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error creating assignment:", error);
      // Server responded with non-2xx status (like 400, 500)
      if (error.response && error.response.data) {
        showErrorToast(error.response.data.message);
      } else {
        showErrorToast("Network error! Please try again.");
      }
    } finally {
      setIsCreateAssignmentLoading(false);
    }
  };

  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  const currentQuestions =
    activeTab === "manual" ? manualQuestions : importQuestions;

  const isEditMode = Boolean(editingAssignment?.assignmentId);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      className="tutor-create-assignment-modal p-0"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="tutor-create-assignment-modal-header">
        <Modal.Title className="tutor-create-assignment-modal-header-title">
          Create New Assignment
        </Modal.Title>
        <button
          className="tutor-create-assignment-close-btn"
          onClick={handleCloseModal}
        >
          ✕
        </button>
      </Modal.Header>

      <Modal.Body className="tutor-create-assignment-modal-body">
        {/* STEP LABEL */}
        <p className="tutor-create-assignment-step-label">
          Step {step} of 3 •{" "}
          {step === 1
            ? "Basic Details"
            : step === 2
              ? "Add Questions"
              : "Review & Finish"}
        </p>

        {/* CUSTOM 3-SEGMENT PROGRESS BAR */}
        <div className="tutor-create-assignment-steps">
          <div
            className={`tutor-create-assignment-step-box ${
              step >= 1 ? "active" : ""
            }`}
          ></div>
          <div
            className={`tutor-create-assignment-step-box ${
              step >= 2 ? "active" : ""
            }`}
          ></div>
          <div
            className={`tutor-create-assignment-step-box ${
              step >= 3 ? "active" : ""
            }`}
          ></div>
        </div>

        {/* STEP 1 CONTENT */}
        {step === 1 && (
          <Form className="tutor-create-assignment-form">
            <Form.Group className="tutor-create-assignment-group">
              <Form.Label>Assignment Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter assignment title"
                name="assignmentTitle"
                value={formData.assignmentTitle}
                onChange={handleCreateAssignmentChange}
                className={`tutor-create-assignment-input ${
                  formData.assignmentTitle ? "filled" : ""
                }`}
                required
              />
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Select Course *</Form.Label>
                  <Form.Select
                    name="course"
                    value={formData.course}
                    onChange={handleCreateAssignmentChange}
                    // onChange={(e) => {
                    //   const selectedCourseId = e.target.value;
                    //   console.log("Course selected:", selectedCourseId);
                    //   handleCreateAssignmentChange(e); er
                    // }}
                    className={`tutor-create-assignment-select ${
                      formData.course ? "filled" : ""
                    }`}
                    required
                  >
                    <option value="">Select Course</option>
                    {Object.keys(courseModules).map((courseId) => (
                      <option key={courseId} value={courseId}>
                        {courseModules[courseId].title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Modules *</Form.Label>
                  <Form.Select
                    name="module"
                    value={formData.module}
                    onChange={handleCreateAssignmentChange}
                    // onChange={(e) => {
                    //   const selectedModuleId = e.target.value;
                    //   console.log("Module selected:", selectedModuleId);
                    //   handleCreateAssignmentChange(e);
                    // }}
                    disabled={!formData.course}
                    className={`tutor-create-assignment-select ${
                      formData.module ? "filled" : ""
                    }`}
                    required
                  >
                    <option value="">Select Modules</option>
                    {formData.course &&
                      courseModules[formData.course].modules.map((module) => (
                        <option key={module.module_id} value={module.module_id}>
                          {module.title}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Assignment Type *</Form.Label>
                  <Form.Select
                    name="assignmentType"
                    value={formData.assignmentType}
                    onChange={handleCreateAssignmentChange}
                    className={`tutor-create-assignment-select ${
                      formData.assignmentType ? "filled" : ""
                    }`}
                    required
                  >
                    <option value="">Select Mcqs</option>
                    <option value="Mcqs">Mcqs</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Total Questions</Form.Label>
                  <Form.Select
                    name="totalQuestions"
                    value={formData.totalQuestions}
                    onChange={handleCreateAssignmentChange}
                    className={`tutor-create-assignment-select ${
                      formData.totalQuestions ? "filled" : ""
                    }`}
                    required
                  >
                    <option value="">Select Questions</option>
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="40">40</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Total Marks *</Form.Label>
                  <Form.Control
                    type="number"
                    name="totalMarks"
                    value={formData.totalMarks}
                    readOnly
                    placeholder="Auto fill based on questions"
                    className={`tutor-create-assignment-input ${
                      formData.totalMarks ? "filled" : ""
                    }`}
                    required
                  />
                </Form.Group>
              </Col>

              <Col>
                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Passing Percentage</Form.Label>
                  <Form.Control
                    type="text"
                    value={`${formData.passingPercentage}%`}
                    readOnly
                    className="tutor-create-assignment-input"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}

        {/* STEP 2 CONTENT */}
        {step === 2 && (
          <div className="tutor-create-assignment-step2">
            <h6 className="tutor-create-assignment-step2-title">
              Add Questions
            </h6>
            <div className="tutor-create-assignment-step2-desc">
              Create question manually or import from file
            </div>

            <div className="tutor-create-assignment-step2-buttons-wrapper">
              <div className="tutor-create-assignment-step2-buttons">
                <Button
                  className={`tutor-create-assignment-step2-tab-btn ${
                    activeTab === "manual" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("manual")}
                >
                  + Add Manually
                </Button>

                <Button
                  className={`tutor-create-assignment-step2-tab-btn ${
                    activeTab === "import" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("import")}
                  disabled={isEditMode}
                >
                  <HiOutlineUpload /> Import
                </Button>
              </div>
            </div>

            {/* MANUAL TAB */}
            {activeTab === "manual" && (
              <div className="tutor-create-assignment-step2-new-question">
                <div className="tutor-create-assignment-step2-new-question-header">
                  <span>New Question</span>
                  <span>#{manualQuestions.length + 1}</span>
                </div>

                <Form.Group className="tutor-create-assignment-group">
                  <Form.Label>Question Text</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your question here..."
                    value={newQuestion.questionText}
                    onChange={handleQuestionChange}
                    className="tutor-create-assignment-step2-question-input"
                  />
                </Form.Group>

                {/* options */}
                <Row className="tutor-create-assignment-step2-options-row">
                  {newQuestion.options.map((opt, idx) => (
                    <Col
                      sm={6}
                      xs={12}
                      key={idx}
                      className="tutor-create-assignment-step2-option-col"
                    >
                      {/* Label */}
                      <label className="tutor-create-assignment-step2-option-label">
                        Option {String.fromCharCode(65 + idx)}
                      </label>

                      {/* Input + Button Row */}
                      <div className="tutor-create-assignment-step2-option-input-row">
                        <Form.Control
                          type="text"
                          value={opt}
                          onChange={(e) =>
                            handleOptionChange(idx, e.target.value)
                          }
                          className="tutor-create-assignment-step2-question-input p-2"
                        />

                        <Button
                          className={`tutor-create-assignment-step2-correct-btn ${
                            newQuestion.correctOptionIndex === idx
                              ? "active"
                              : ""
                          }`}
                          onClick={() => setCorrectOption(idx)}
                        >
                          <FaRegCheckCircle className="tutor-create-assignment-step2-correct-icon" />
                        </Button>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className="tutor-create-assignment-step2-add-question-wrapper">
                  <Button
                    className="tutor-create-assignment-step2-add-question-btn"
                    onClick={addManualQuestion}
                  >
                    {editingIndex !== null
                      ? "Update Question"
                      : "+ Add Question"}
                  </Button>
                </div>
              </div>
            )}

            {activeTab === "import" && (
              <div className="tutor-create-assignment-step2-import-wrapper">
                <div className="tutor-create-assignment-step2-import-box">
                  <div className="tutor-create-assignment-step2-import-icon">
                    <HiOutlineUpload />
                  </div>
                  <h6 className="tutor-create-assignment-step2-import-title">
                    Import Questions from File
                  </h6>
                  <p className="tutor-create-assignment-step2-import-desc">
                    Upload a CSV file with your questions
                  </p>
                  <Button className="tutor-create-assignment-step2-import-choose-btn">
                    Choose Files
                  </Button>

                  <hr className="tutor-create-assignment-step2-import-hr" />
                  <div className="tutor-create-assignment-step2-import-template-wrapper">
                    <span>Sample CSV File</span>
                    <Button
                      className="tutor-create-assignment-step2-import-template-btn"
                      as="a"
                      href={TemplateCSV}
                      download="AssignmentTemplate.csv"
                    >
                      <HiOutlineDownload /> CSV Template
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Questions list */}
            {currentQuestions.length > 0 && (
              <div className="tutor-create-assignment-step2-list-header">
                <span> ≡ {currentQuestions.length} Questions Added</span>

                {!isEditMode && (
                  <button
                    className="tutor-create-assignment-step2-list-header-clear-all-btn"
                    onClick={() =>
                      activeTab === "manual"
                        ? setManualQuestions([])
                        : setImportQuestions([])
                    }
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}

            {currentQuestions.length === 0 ? (
              <div className="tutor-create-assignment-step2-empty">
                <div className="tutor-create-assignment-step2-empty-icon">
                  ≡
                </div>
                <h5>No questions added yet</h5>
                <p>Use the tabs above to add questions</p>
              </div>
            ) : (
              <div className="tutor-create-assignment-step2-empty p-3 tutor-create-assignment-step2-question-list-wrapper">
                {currentQuestions.map((q, i) => (
                  <div
                    className="tutor-create-assignment-step2-question-card"
                    key={i}
                  >
                    {!isEditMode && (
                      <button
                        className="tutor-create-assignment-step2-remove-question-btn"
                        onClick={() =>
                          activeTab === "manual"
                            ? removeManualQuestion(i)
                            : removeImportQuestion(i)
                        }
                      >
                        ✕
                      </button>
                    )}

                    {isEditMode && (
                      <FiEdit
                        className="tutor-create-assignment-step3-q-edit"
                        onClick={() => handleEditQuestion(i)}
                      />
                    )}

                    <div className="tutor-create-assignment-step2-question-card-header">
                      <div>
                        <span className="tutor-create-assignment-step2-question-number">{`0${
                          i + 1
                        }`}</span>
                      </div>
                      <span className="tutor-create-assignment-step2-question-text">
                        {q.questionText}
                      </span>
                    </div>

                    {/* OPTIONS */}
                    <div className="tutor-create-assignment-step2-question-card-options">
                      {q.options.map((opt, idx) => (
                        <div
                          className="tutor-create-assignment-step2-question-card-option-row"
                          key={idx}
                        >
                          <FaRegCheckCircle
                            className={`tutor-create-assignment-step2-question-option-icon ${
                              q.correctOptionIndex === idx
                                ? "correct"
                                : "incorrect"
                            }`}
                          />

                          <span className="tutor-create-assignment-step2-question-card-option-text">
                            {opt}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 3 CONTENT */}
        {step === 3 && (
          <div className="tutor-create-assignment-step3">
            <div className="tutor-create-assignment-step3-header">
              <FaRegCheckCircle className="tutor-create-assignment-step3-icon" />
              <h4 className="tutor-create-assignment-step3-title">
                Review & Publish
              </h4>
              <p className="tutor-create-assignment-step3-subtitle">
                Review your assignment before publishing
              </p>
            </div>

            <div className="tutor-create-assignment-step3-details-card">
              <div className="tutor-create-assignment-step3-section-header">
                Assignment Details
              </div>

              <div className="tutor-create-assignment-step3-grid">
                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Title
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    {formData.assignmentTitle}
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Course
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    {courseModules[formData.course]?.title || formData.course}
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Module
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    {courseModules[formData.course]?.modules.find(
                      (m) => m.module_id === Number(formData.module),
                    )?.title || formData.module}
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Type
                  </span>
                  <span className="tutor-create-assignment-step3-grid-mcqs">
                    {formData.assignmentType}
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Questions
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    {manualQuestions.length}
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Total Marks
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    {formData.totalMarks}
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Passing Percentage
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    {formData.passingPercentage}%
                  </span>
                </div>

                <div className="tutor-create-assignment-step3-grid-item">
                  <span className="tutor-create-assignment-step3-grid-label">
                    Submission Date
                  </span>
                  <span className="tutor-create-assignment-step3-grid-value">
                    -
                  </span>
                </div>
              </div>
            </div>

            {/* QUESTIONS PREVIEW */}
            <div className="tutor-create-assignment-step3-details-card">
              <div className="tutor-create-assignment-step3-section-header d-flex justify-content-between mb-3">
                Questions Preview
                <span className="tutor-create-assignment-step3-questions-count">
                  Showing all {manualQuestions.length}
                </span>
              </div>

              <div className="tutor-create-assignment-step3-questions-scroll">
                {manualQuestions.map((q, i) => (
                  <div
                    key={i}
                    className="tutor-create-assignment-step3-question-card"
                  >
                    <div className="tutor-create-assignment-step3-question-card-inner">
                      <div className="tutor-create-assignment-step3-q-header">
                        <div className="tutor-create-assignment-step3-q-number">
                          {i < 9 ? `0${i + 1}` : i + 1}
                        </div>
                        <div className="tutor-create-assignment-step3-q-text">
                          {q.questionText}
                        </div>
                        <FiEdit
                          className="tutor-create-assignment-step3-q-edit"
                          onClick={() => handleEditQuestion(i)}
                        />
                      </div>

                      <div className="tutor-create-assignment-step3-q-options">
                        {q.options.map((opt, idx) => (
                          <div
                            key={idx}
                            className={`tutor-create-assignment-step3-q-option ${
                              q.correctOptionIndex === idx ? "correct" : ""
                            }`}
                          >
                            <FaRegCheckCircle
                              className={`tutor-create-assignment-step2-question-option-icon ${
                                q.correctOptionIndex === idx
                                  ? "correct"
                                  : "incorrect"
                              }`}
                            />

                            <label className="tutor-create-assignment-step2-question-option-label">
                              {opt}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="tutor-create-assignment-modal-footer">
        {!(editingAssignment && step === 2) && (
          <Button
            className="tutor-create-assignment-cancel-btn"
            onClick={step === 1 ? handleCloseModal : prevStep}
          >
            {step === 1 ? "Cancel" : "Back"}
          </Button>
        )}

        {step === 1 && (
          <Button
            className="tutor-create-assignment-next-btn"
            onClick={handleNextStep}
          >
            Next Step
          </Button>
        )}

        {step === 2 && (
          <Button
            className="tutor-create-assignment-next-btn"
            onClick={
              editingAssignment && editingAssignment.assignmentId
                ? handleUpdateRejectedQuestion
                : handleSaveQuestions
            }
            disabled={isUpdateQuestionLoading}
          >
            {editingAssignment && editingAssignment.assignmentId
              ? isUpdateQuestionLoading
                ? "Updating..." // show while loading
                : "Update Question"
              : "Save Question"}
          </Button>
        )}

        {/* {step === 3 && (
          <Button
            className="tutor-create-assignment-save-draft-btn"
            onClick={handleSaveAsDraft}
            disabled={isCreateAssignmentLoading}
          >
            {isCreateAssignmentLoading ? "Saving..." : "Save As Draft"}
          </Button>
        )} */}

        {step === 3 && (
          <Button
            className="tutor-create-assignment-publish-btn"
            onClick={handleCreateAssignment}
            disabled={isCreateAssignmentLoading}
          >
            {isCreateAssignmentLoading ? "Creating..." : "Create Assignment"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TutorCreateAssignments;
